import { Flex } from "~/components/Flex";
import { ButtonProps, Typography } from "@mui/material";
import { ConditionTagValue } from "~/components/ConditionBuilder/ConditionTagValue/ConditionTagValue";
import { IndentIcon } from "~/icons";
import { useConditionRow } from "./hooks/useConditionRow";
import {
  ConditionValue,
  WorkspaceConditionValues,
} from "~/pages/space/workspaces/components/ConditionBuilder/components/WorkspaceConditionValues";
import { WorkspaceConditionKeysAutocomplete } from "~/pages/space/workspaces/components/ConditionBuilder/components/ConditionAutocomplete/WorkspaceConditionKeysAutocomplete";
import { WorkspaceConditionOperator } from "~/pages/space/workspaces/components/ConditionBuilder/components/ConditionOperator/WorkspaceConditionOperator";

interface WorkspaceConditionRowProps {
  selectionIndex: number;
  conditionIndex: number;
  onRemove: ButtonProps["onClick"];
  isReadonly: boolean;
}

export const WorkspaceConditionRow = ({
  conditionIndex,
  selectionIndex,
  onRemove,
  isReadonly,
}: WorkspaceConditionRowProps) => {
  const {
    showIncludeLabel,
    showConditionOperator,
    showConditionKeyLabel,
    isKeySelected,
    isDictionaryKeySelected,
    conditionKeys,
    conditionValues,
    conditionValuesOptions,
    selectedConditionKeyLabel,
    conditionValuesFieldName,
    conditionFormDictionaryKey,
    handleConditionValueRemove,
    handleConditionKeyRemove,
    handleKeyChange,
    handleValuesItemClick,
    handleDropdownSearchFieldKeyUp,
    handleDropdownSearchFieldChange,
    isLoading,
    isValuesDropdownOpen,
    handleValuesDropdownOpen,
    handleValuesDropdownClose,
    handleKeyValueKeyClick,
    handleKeyValueValueClick,
  } = useConditionRow({
    selectionIndex,
    conditionIndex,
    isReadonly,
  });

  return (
    <Flex
      gap={2}
      alignItems="flex-start"
      sx={{
        "&:hover .delete-condition-button": {
          visibility: "visible",
        },
      }}
    >
      {showIncludeLabel && (
        <Typography sx={{ fontWeight: 700, p: 1 }}>INCLUDE:</Typography>
      )}
      {showConditionOperator && (
        <Flex alignItems="center" gap={2} ml={8}>
          <IndentIcon sx={{ color: (theme) => theme.palette.text.disabled }} />
          <WorkspaceConditionOperator
            selectionIndex={selectionIndex}
            conditionIndex={conditionIndex}
            isReadonly={isReadonly}
            type="condition"
          />
        </Flex>
      )}
      {showConditionKeyLabel ? (
        <ConditionTagValue
          value={selectedConditionKeyLabel || ""}
          onClick={handleConditionKeyRemove}
          isReadonly={isReadonly}
        />
      ) : (
        conditionValues.length === 0 &&
        !isReadonly && (
          <WorkspaceConditionKeysAutocomplete
            name={`selections.${selectionIndex}.conditions.${conditionIndex}.formKey`}
            groups={conditionKeys}
            onChange={handleKeyChange}
          />
        )
      )}
      {isKeySelected && (
        <WorkspaceConditionOperator
          selectionIndex={selectionIndex}
          conditionIndex={conditionIndex}
          isReadonly={isReadonly}
        />
      )}
      <WorkspaceConditionValues
        values={conditionValues as ConditionValue[]}
        options={conditionValuesOptions}
        showRemoveConditionButton={conditionIndex !== 0 && !isReadonly}
        onRemove={onRemove}
        isKeySelected={isKeySelected}
        isDictionaryKeySelected={isDictionaryKeySelected}
        onValueRemove={handleConditionValueRemove}
        name={conditionValuesFieldName}
        conditionFormDictionaryKey={conditionFormDictionaryKey}
        isReadonly={isReadonly}
        isLoading={isLoading}
        onDropdownSearchFieldKeyUp={handleDropdownSearchFieldKeyUp}
        onDropdownSearchFieldChange={handleDropdownSearchFieldChange}
        onItemClick={handleValuesItemClick}
        handleKeyValueKeyClick={handleKeyValueKeyClick}
        handleKeyValueValueClick={handleKeyValueValueClick}
        isValuesDropdownOpen={isValuesDropdownOpen}
        onValuesDropdownOpen={handleValuesDropdownOpen}
        onValuesDropdownClose={handleValuesDropdownClose}
      />
    </Flex>
  );
};
